import { PayloadErrandAction } from '../../payload/payload-types.zod'
import { getId } from '../../payload/payload.helpers'
import z from 'zod'

export const ErrandActionDbSchema = PayloadErrandAction

const metadataSchema = z
  .union([
    z.record(z.string(), z.unknown()),
    z.array(z.unknown()),
    z.string(),
    z.number(),
    z.boolean(),
  ])
  .nullable()
  .optional()

/**
 * Internal create shape — used by the AI tools / service when persisting a generated action.
 * `errand`, `individual` and `user` are supplied by the service, not the caller.
 */
export const ErrandActionCreateRequestSchema = z.object({
  type: PayloadErrandAction.shape.type,
  title: z.string(),
  content: z.string().nullable().optional(),
  metadata: metadataSchema,
  sourceThread: z.string().optional(),
  sourceMessageId: z.string().optional(),
})

export const ErrandActionResponseSchema = z.object({
  id: z.string(),
  errand: z.string(),
  type: PayloadErrandAction.shape.type,
  title: z.string(),
  content: z.string().nullable().optional(),
  metadata: metadataSchema,
  sourceThread: z.string().nullable().optional(),
  sourceMessageId: z.string().nullable().optional(),
  updatedAt: z.string(),
  createdAt: z.string(),
})

export const ErrandActionListResponseSchema = ErrandActionResponseSchema.array()

export type ErrandActionDb = z.infer<typeof ErrandActionDbSchema>
export type ErrandActionCreateRequest = z.infer<typeof ErrandActionCreateRequestSchema>
export type ErrandActionResponse = z.infer<typeof ErrandActionResponseSchema>
export type ErrandActionListResponse = z.infer<typeof ErrandActionListResponseSchema>

const toResponseCandidate = (data: ErrandActionDb) => ({
  id: data.id,
  errand: getId(data.errand),
  type: data.type,
  title: data.title,
  content: data.content,
  metadata: data.metadata,
  sourceThread: data.sourceThread ? getId(data.sourceThread) : undefined,
  sourceMessageId: data.sourceMessageId,
  updatedAt: data.updatedAt,
  createdAt: data.createdAt,
})

export const errandActionDbToResponse = (data: ErrandActionDb): ErrandActionResponse => {
  return ErrandActionResponseSchema.parse(toResponseCandidate(data) satisfies ErrandActionResponse)
}

/**
 * List-safe variant: returns `undefined` instead of throwing when a stored row no longer matches
 * the schema. `type` is a closed enum, so a row written under a draft type that has since been
 * retired (`checklist`) would otherwise fail the parse and take the whole errand-actions list with
 * it — one legacy row must not break the errand page. Use `errandActionDbToResponse` on the write
 * path, where a shape mismatch is a real bug worth surfacing.
 */
export const errandActionDbToResponseOrUndefined = (
  data: ErrandActionDb,
): ErrandActionResponse | undefined => {
  const result = ErrandActionResponseSchema.safeParse(toResponseCandidate(data))

  return result.success ? result.data : undefined
}
